import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  LinearProgress,
  CircularProgress,
  Tooltip,
  IconButton,
  Chip,
} from "@mui/material";
import {
  HelpOutline,
  CheckCircle,
  Error as ErrorIcon,
  Receipt as ReceiptIcon,
} from "@mui/icons-material";
import api from "../../services/api";

// インボイス登録番号のバリデーション
const validateInvoiceNumber = (number) => {
  if (!number) return { valid: true, message: "" };

  // T + 13桁の数字
  const pattern = /^T[0-9]{13}$/;
  if (!pattern.test(number)) {
    return { valid: false, message: "登録番号は「T」+ 13桁の数字で入力してください" };
  }

  // チェックディジット検証（法人番号と同じアルゴリズム）
  const digits = number.slice(1); // T を除く
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    const weight = i % 2 === 0 ? 1 : 2;
    sum += parseInt(digits[i], 10) * weight;
  }
  const checkDigit = (9 - (sum % 9)) % 9;
  const lastDigit = parseInt(digits[12], 10);

  if (checkDigit !== lastDigit) {
    return { valid: false, message: "チェックディジットが不正です" };
  }

  return { valid: true, message: "有効な登録番号です" };
};

const InvoiceSettings = () => {
  const { storeId } = useParams();
  const [settings, setSettings] = useState({
    invoiceRegistrationNumber: "",
    invoiceBusinessName: "",
    isQualifiedInvoiceIssuer: false,
    invoiceRegistrationDate: "",
  });
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validation, setValidation] = useState({ valid: true, message: "" });

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/stores/${storeId}/invoice-settings`);
      setSettings((prev) => ({ ...prev, ...response.data }));
    } catch (err) {
      console.error("インボイス設定の取得に失敗しました:", err);
      // 404の場合は初期値を使用
      if (err.response?.status !== 404) {
        setError("インボイス設定の取得に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  useEffect(() => {
    const result = validateInvoiceNumber(settings.invoiceRegistrationNumber);
    setValidation(result);
  }, [settings.invoiceRegistrationNumber]);

  const handleChange = (field) => (e) => {
    const value =
      e.target.type === "checkbox"
        ? e.target.checked
        : e.target.value;
    setSettings((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (settings.invoiceRegistrationNumber && !validation.valid) {
      setError("登録番号を正しく入力してください");
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await api.put(`/api/stores/${storeId}/invoice-settings`, settings);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("インボイス設定の更新に失敗しました:", err);
      setError(err.response?.data?.message || "インボイス設定の更新に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box aria-busy={loading || saving}>
      {loading && <LinearProgress sx={{ mb: 2 }} />}
      <Typography variant="h4" gutterBottom>
        インボイス制度設定
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        適格請求書発行事業者の登録番号と関連設定を行います。
        インボイス制度（適格請求書等保存方式）に対応するための設定です。
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          設定を更新しました
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                  <ReceiptIcon color="primary" />
                  <Typography variant="h6">適格請求書発行事業者登録</Typography>
                </Box>

                <Grid container spacing={2}>
                  {/* 適格請求書発行事業者フラグ */}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.isQualifiedInvoiceIssuer}
                          onChange={handleChange("isQualifiedInvoiceIssuer")}
                          disabled={saving}
                        />
                      }
                      label="適格請求書発行事業者として登録済み"
                    />
                    <Typography variant="caption" color="text.secondary" display="block">
                      国税庁に適格請求書発行事業者として登録している場合はONにしてください
                    </Typography>
                  </Grid>

                  {/* 登録番号 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="登録番号"
                      value={settings.invoiceRegistrationNumber || ""}
                      onChange={handleChange("invoiceRegistrationNumber")}
                      disabled={saving || !settings.isQualifiedInvoiceIssuer}
                      placeholder="T1234567890123"
                      error={settings.invoiceRegistrationNumber && !validation.valid}
                      helperText={
                        settings.invoiceRegistrationNumber
                          ? validation.message
                          : "「T」+ 13桁の数字（例: T1234567890123）"
                      }
                      InputProps={{
                        endAdornment: settings.invoiceRegistrationNumber && (
                          validation.valid ? (
                            <CheckCircle color="success" />
                          ) : (
                            <ErrorIcon color="error" />
                          )
                        ),
                      }}
                    />
                  </Grid>

                  {/* 登録日 */}
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="登録日"
                      type="date"
                      value={settings.invoiceRegistrationDate || ""}
                      onChange={handleChange("invoiceRegistrationDate")}
                      disabled={saving || !settings.isQualifiedInvoiceIssuer}
                      InputLabelProps={{ shrink: true }}
                      helperText="適格請求書発行事業者として登録された日"
                    />
                  </Grid>

                  {/* 事業者名 */}
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="インボイス記載事業者名"
                      value={settings.invoiceBusinessName || ""}
                      onChange={handleChange("invoiceBusinessName")}
                      disabled={saving || !settings.isQualifiedInvoiceIssuer}
                      placeholder="株式会社○○○○"
                      helperText="インボイス（適格請求書）に記載する事業者名称"
                      InputProps={{
                        endAdornment: (
                          <Tooltip title="国税庁に届け出た事業者名を入力してください">
                            <IconButton size="small">
                              <HelpOutline fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ),
                      }}
                    />
                  </Grid>

                  {/* 説明 */}
                  <Grid item xs={12}>
                    <Alert severity="info">
                      <Typography variant="body2">
                        <strong>インボイス制度について</strong>
                        <br />
                        2023年10月1日から開始された適格請求書等保存方式（インボイス制度）に対応するための設定です。
                        適格請求書発行事業者として登録されている場合、レシートに登録番号が印字されます。
                      </Typography>
                    </Alert>
                  </Grid>

                  {/* 登録番号確認リンク */}
                  {settings.invoiceRegistrationNumber && validation.valid && (
                    <Grid item xs={12}>
                      <Chip
                        label="国税庁の公表サイトで登録番号を確認"
                        component="a"
                        href={`https://www.invoice-kohyo.nta.go.jp/regno-search/detail?regno=${settings.invoiceRegistrationNumber}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        clickable
                        color="primary"
                        variant="outlined"
                      />
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* 保存ボタン */}
          <Grid item xs={12}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={saving || (settings.invoiceRegistrationNumber && !validation.valid)}
            >
              {saving ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "設定を保存"
              )}
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};

export default InvoiceSettings;
